import { openEvolutionModal, responseData } from 'Pages/AnalyzersV2/@signals'

// Targets and actions shared by the five analyzer Virtual Tours (LOR, DOR, Brokers, Direct Site, Rating).
// Every target is a data-tour-id that the real page and its tour DemoData both carry, so a step
// spotlights the same element with real data and with the demo.

const byTourId = (id, root = document) => root?.querySelector(`[data-tour-id="${id}"]`) || null

export const hasRealData = () => Object.keys(responseData.value || {}).length > 0

export const reportSelector = () => byTourId('tour-report-selector')

export const analyzerLinks = () => {
	const el = byTourId('tour-analyzer-links')
	return el?.children.length ? el : null
}

export const priceTable = () => byTourId('tour-price-table')

// The "Show details / Hide details" selector of the table toolbar
export const detailsToggle = () =>
	[...(priceTable()?.querySelectorAll('[role="group"]') || [])].find(g => /details/i.test(g.textContent)) || null

export const featuresSelector = () => byTourId('tour-features-selector', priceTable())

export const minMaxToggle = () => byTourId('tour-minmax-toggle', priceTable())

// The Cars selector of DOR / LOR (CU-869fmpqfm): the second row of the table's header
export const carPickField = () => byTourId('tour-car-pick', priceTable())

// First price cell that reacts to a click (N/A cells do not): the demo marks its own,
// in the real table it is the first cell whose clickable box has the pointer cursor.
export const priceCell = () => {
	const table = priceTable()
	if (!table) return null
	const demo = byTourId('tour-price-cell', table)
	if (demo) return demo
	for (const td of table.querySelectorAll('tbody td:not(:first-child)')) {
		const box = td.firstElementChild
		if (box && getComputedStyle(box).cursor === 'pointer') return td
	}
	return null
}

const clickPriceCell = () => priceCell()?.firstElementChild?.click()

// What the spotlight frames: the cell's bordered box, not the whole (maybe very wide) table cell
export const priceCellBox = () => {
	const td = priceCell()
	return td?.firstElementChild?.firstElementChild || td
}

export const cellPopover = () => byTourId('tour-demo-popover') || document.querySelector('.MuiPopover-paper')

export const popoverButton = id => byTourId(id, cellPopover())

// The real popover only has "Real time" / "Screenshot" when the cell has a linked search / a screenshot.
// What the open popover has is remembered, so the steps still know it once the popover is closed
// (the last popover step closes it before the engine checks the next step's skipIf, and Back re-opens it later).
let popoverHas = {}
export const missingInPopover = id => {
	if (!hasRealData()) return false
	if (cellPopover()) popoverHas = Object.fromEntries(
		['tour-popover-evolution', 'tour-popover-realtime', 'tour-popover-screenshot'].map(b => [b, !!popoverButton(b)])
	)
	return popoverHas[id] === false
}

export const openCellPopover = demoPopoverOpen => {
	if (hasRealData()) clickPriceCell()
	else demoPopoverOpen.value = true
}

export const closeCellPopover = demoPopoverOpen => {
	if (demoPopoverOpen) demoPopoverOpen.value = false
	byTourId('tour-popover-close', cellPopover())?.click()
}

// For the popover button steps: Next closes the popover when none of the later buttons is there
export const closePopoverIfLast = (demoPopoverOpen, ...laterIds) => () => {
	if (laterIds.every(id => missingInPopover(id))) closeCellPopover(demoPopoverOpen)
}

// Rating: a cell opens its evolution chart directly
export const openCellEvolution = () => {
	if (hasRealData()) clickPriceCell()
	else openEvolutionModal.value = { demoMode: true }
}

export const columnHeader = () => byTourId('tour-column-header', priceTable())?.closest('th') || null

export const openColumnEvolution = () => {
	if (hasRealData()) byTourId('tour-column-header', priceTable())?.click()
	else openEvolutionModal.value = { demoMode: true }
}

export const evolutionModal = () => document.querySelector('.MuiDialog-paperFullScreen')

export const closeEvolutionModal = () => { openEvolutionModal.value = {} }

export const isEvolutionModalOpen = () => Object.keys(openEvolutionModal.value || {}).length > 0 && !!evolutionModal()

export const pricesChart = () => byTourId('tour-prices-chart')

export const chartFullscreen = () => byTourId('tour-chart-fullscreen', pricesChart())

export const categoriesTable = () => byTourId('tour-categories-table')
